"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Loader, Tag, Folder, CircleAlert } from "lucide-react";

function ProductDetailConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();

  if (loading) {
    return (
      <div data-section-id="32545" className="container mx-auto px-4 py-12 min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground font-sans">Initializing secure access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 min-h-[40vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <CircleAlert className="h-10 w-10 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold font-default mb-2">Connection Error</h2>
          <p className="text-muted-foreground font-sans mb-4">We were unable to retrieve the digital service details. Please verify your connection.</p>
          <button 
            onClick={() => refetch()} 
            className="text-primary underline hover:text-primary/80 font-default font-medium transition-colors"
          >
            Reconnect
          </button>
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 min-h-[40vh] flex items-center justify-center">
        <p className="text-muted-foreground font-sans">Service unavailable.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount = product.discounted_price !== undefined && product.discounted_price < product.price;

  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16">
        {}
        <div className="w-full">
          <div className="relative aspect-square w-full overflow-hidden rounded-xl border border-border bg-card/50 shadow-lg">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover object-center transition-transform duration-700 hover:scale-105"
                loading="eager"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-sm font-sans">No preview available</span>
              </div>
            )}
            
            {hasDiscount && (
              <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-sm text-xs font-bold font-default tracking-wider shadow-xl">
                FEATURED
              </div>
            )}
          </div>
        </div>

        {}
        <div className="flex flex-col h-full">
          <div className="mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tighter mb-4 font-default">
              {product.title}
            </h1>
            
            <div className="flex items-baseline gap-3 mb-6">
              {hasDiscount ? (
                <>
                  <span className="text-3xl font-bold text-primary font-default">
                    ${product.discounted_price?.toFixed(2)}
                  </span>
                  <span className="text-xl text-muted-foreground line-through decoration-destructive/50 font-default">
                    ${product.price.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold text-foreground font-default">
                  ${product.price?.toFixed(2)}
                </span>
              )}
            </div>

            {product.short_description && (
              <div 
                className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground mb-8 leading-relaxed font-sans"
                dangerouslySetInnerHTML={{ __html: product.short_description }}
              />
            )}
          </div>

          <div className="bg-card/50 border border-border rounded-lg p-6 shadow-md mb-8 backdrop-blur-sm">
            <AddToCartButton 
              productId={product.id} 
              variant="default" 
              className="w-full" 
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base font-bold font-default w-full transition-all shadow-lg hover:shadow-primary/20 rounded-sm" 
              text="Add to Cart" 
              showQuantity={true} 
            />
          </div>

          <div className="space-y-4 text-sm text-muted-foreground mt-auto">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-start gap-2">
                <Folder className="h-4 w-4 mt-1 shrink-0 text-primary" />
                <span className="font-bold text-foreground mr-1 font-default">Service Type:</span>
                <div className="flex flex-wrap gap-2">
                  {product.categories.map((cat, i) => (
                    <Link 
                      key={cat.id} 
                      to={cat.link} 
                      data-index={i}
                      className="hover:text-primary transition-colors underline decoration-transparent hover:decoration-primary underline-offset-4 font-sans"
                    >
                      {cat.name}
                      {i < product.categories.length - 1 && ","}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-2">
                <Tag className="h-4 w-4 mt-1 shrink-0 text-primary" />
                <span className="font-bold text-foreground mr-1 font-default">Attributes:</span>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, i) => (
                    <Link 
                      key={tag.id} 
                      to={tag.link} 
                      data-index={i}
                      className="bg-muted hover:bg-muted/80 px-2 py-0.5 rounded-sm text-xs transition-colors font-sans border border-border/50"
                    >
                      {tag.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Separator className="my-12 md:my-16 opacity-50" />

      {}
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-foreground mb-6 pb-2 border-b border-primary/30 inline-block font-default tracking-tight">
          Technical Specifications
        </h2>
        {product.long_description ? (
          <div 
            className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-bold prose-headings:font-default prose-a:text-primary prose-img:rounded-lg font-sans"
            dangerouslySetInnerHTML={{ __html: product.long_description }}
          />
        ) : (
          <p className="text-muted-foreground italic font-sans">No detailed specifications provided for this digital asset.</p>
        )}
      </div>
    </div>
  );
}

export default function SingleProductTemplateSection() {
  
  let wp_query;
  try {
    if (typeof window !== "undefined" && window.wvcClient) {
      const current_wp_query_params = window.wvcClient.getCurrentWPQueryParams();
      wp_query = new WP_Query(current_wp_query_params);
    }
  } catch (e) {
    console.error("Error initializing WP_Query:", e);
  }

  return (
    <section id="single-product-template-section" className="dark bg-background text-foreground w-full">
      <EcommerceProductsProvider wp_query={wp_query} className="w-full" postType="product">
        <ProductDetailConsumer />
      </EcommerceProductsProvider>
    </section>
  );
}